/**
 * Which tenant (or the platform console) a browser host belongs to.
 *
 * `slug.<root domain>` is a tenant, `admin.<root domain>` is the platform
 * console. With no root domain configured (today: a single tenant, local
 * development, preview deployments) every host is the default tenant.
 */
export type HostContext = { kind: "tenant"; slug: string } | { kind: "platform" };

export type HostInput = {
  hostname: string;
  /** `location.search`, only consulted when `allowOverride` is true. */
  search: string;
  /** Development only: `?tenant=<slug>` and `?platform=1` pick the context. */
  allowOverride: boolean;
  devTenant?: string | undefined;
  defaultSlug: string;
  rootDomain?: string | undefined;
};

const RESERVED = new Set(["www", "api", "app", "mail", "static"]);

export function resolveHost(input: HostInput): HostContext {
  if (input.allowOverride) {
    const params = new URLSearchParams(input.search);
    if (params.get("platform") === "1") {
      return { kind: "platform" };
    }
    const override = params.get("tenant") ?? input.devTenant;
    if (override) {
      return { kind: "tenant", slug: override.toLowerCase() };
    }
  }

  const root = input.rootDomain?.toLowerCase();
  const host = input.hostname.toLowerCase();
  if (root && host.endsWith(`.${root}`)) {
    const label = host.slice(0, -(root.length + 1)).split(".").pop() ?? "";
    if (label === "admin") {
      return { kind: "platform" };
    }
    if (label !== "" && !RESERVED.has(label)) {
      return { kind: "tenant", slug: label };
    }
  }
  return { kind: "tenant", slug: input.defaultSlug };
}
